import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ZONES, WORLD } from '../config/zones';
import { mulberry32 } from './rng';
import { shared } from './shared';
import { useStore } from '../store';

const body = new THREE.Object3D();
const partObj = new THREE.Object3D();
const M = new THREE.Matrix4();

// Predator patrol perimeters (mid-tier zone clearings, both banks)
const CLEARINGS = ZONES.filter((z, i) => i >= 2 && i <= 7).flatMap((z) => [
  new THREE.Vector3(z.x, 0, -60),
  new THREE.Vector3(z.x, 0, 60),
]);
// Contested artifacts drifting in the river that piranhas swarm
export const ARTIFACTS = ZONES.filter((z, i) => i % 3 === 1).map((z) => new THREE.Vector3(z.x, 0.4, 0));

function Geo({ shape, args }) {
  if (shape === 'sphere') return <sphereGeometry args={args} />;
  if (shape === 'cone') return <coneGeometry args={args} />;
  if (shape === 'cyl') return <cylinderGeometry args={args} />;
  return <boxGeometry args={args} />;
}

// One species rendered entirely with InstancedMesh — every body part is a single
// instanced draw call shared by the whole population. Lightweight per-agent AI.
export default function InstancedSpecies({ parts, count, bounds, seed, behavior = 'wander', pop = false, flee = false, speciesKey, registerTo }) {
  const refs = useRef([]);
  const rand = useMemo(() => mulberry32(seed), [seed]);

  const agents = useMemo(() => {
    return Array.from({ length: count }).map(() => {
      const x = THREE.MathUtils.lerp(bounds.xMin, bounds.xMax, rand());
      let z = THREE.MathUtils.lerp(bounds.zMin, bounds.zMax, rand());
      if (behavior === 'patrol') z = (rand() > 0.5 ? 1 : -1) * (9 + rand() * 3);
      if (behavior === 'eel') z = (rand() > 0.5 ? 1 : -1) * (9 + rand() * 4);
      if (behavior === 'whale') z = (rand() - 0.5) * 6;
      const pos = new THREE.Vector3(x, bounds.y, z);
      const rr = 6 + rand() * (behavior === 'canopyfly' ? 12 : 6);
      return {
        pos,
        target: pos.clone(),
        yaw: rand() * Math.PI * 2,
        phase: rand() * Math.PI * 2,
        speed: (behavior === 'swim' || behavior === 'swarm' ? 6 : behavior === 'stalk' || behavior === 'hunt' ? 3.4 : behavior === 'patrol' ? 6 : behavior === 'whale' ? 1.1 : behavior === 'eel' ? 9 : behavior === 'school' ? 3 : 1.4) + rand() * 1.8,
        angle: rand() * Math.PI * 2,
        r: rr, baseR: rr,
        center: behavior === 'canopyfly'
          ? new THREE.Vector3(x, 0, z)
          : behavior === 'school'
            ? new THREE.Vector3(x, bounds.y, (rand() > 0.5 ? 1 : -1) * (13 + rand() * 5))
            : (CLEARINGS.length ? CLEARINGS[Math.floor(rand() * CLEARINGS.length)].clone() : new THREE.Vector3()),
        dir: rand() > 0.5 ? 1 : -1,
        baseZ: z,
        baseY: behavior === 'canopyfly' ? 14 + rand() * 8 : bounds.y + (behavior === 'perch' ? rand() * 5 : 0),
        y: bounds.y,
        rotX: 0, rotZ: 0, s: 1, snap: 0,
        dashUntil: 0, retargetAt: 0,
      };
    });
  }, [count, seed, behavior]); // eslint-disable-line react-hooks/exhaustive-deps

  // Register live positions so predators/humans can track this herd.
  useMemo(() => { if (registerTo) agents.forEach((a) => registerTo.push(a.pos)); }, [registerTo, agents]);
  // Register into the global fauna registry so the Field Guide can identify a fly-by.
  useMemo(() => { if (speciesKey) agents.forEach((a) => shared.fauna.push({ pos: a.pos, key: speciesKey })); }, [speciesKey, agents]);

  const localMats = useMemo(() => parts.map((pt) => {
    partObj.position.set(...(pt.pos || [0, 0, 0]));
    partObj.rotation.set(...(pt.rot || [0, 0, 0]));
    partObj.scale.set(...(pt.scale || [1, 1, 1]));
    partObj.updateMatrix();
    return partObj.matrix.clone();
  }), [parts]);

  const moveToward = (a, speed, dt) => {
    const dx = a.target.x - a.pos.x;
    const dz = a.target.z - a.pos.z;
    const d = Math.hypot(dx, dz);
    if (d > 0.05) {
      const step = Math.min(speed * dt, d) / d;
      a.pos.x += dx * step;
      a.pos.z += dz * step;
      a.yaw = THREE.MathUtils.lerp(a.yaw, Math.atan2(dx, dz), 0.12);
    }
  };

  useFrame((state, delta) => {
    for (let pi = 0; pi < parts.length; pi++) if (!refs.current[pi]) return; // all part meshes mounted?
    const dt = Math.min(delta, 0.05);
    const now = state.clock.elapsedTime;
    const echoActive = behavior === 'canopyfly' && useStore.getState().narrating && (Date.now() - shared.briefT < 14000);
    if (behavior === 'swim' && speciesKey === 'dolphin') shared.dolphinMaxZ = -999;

    for (let i = 0; i < count; i++) {
      const a = agents[i];
      a.rotX = 0; a.rotZ = 0;

      if (behavior === 'hunt') {
        // Actively hunt the nearest grazer (donkeys + elephants): lock on, sprint, pounce.
        let tp = null, bd = Infinity;
        const scan = (arr) => { for (const pr of arr) { const dx = pr.x - a.pos.x, dz = pr.z - a.pos.z; const d = dx * dx + dz * dz; if (d < bd) { bd = d; tp = pr; } } };
        scan(shared.herds.donkeys); scan(shared.herds.elephants);
        let speed = a.speed;
        if (tp) {
          const dist = Math.sqrt(bd);
          a.target.set(tp.x, bounds.y, tp.z);
          speed = a.speed * (dist < 32 ? 2.7 : 1.7);
          a.s = dist < 4.5 ? 1 + Math.abs(Math.sin(now * 14)) * 0.28 : THREE.MathUtils.lerp(a.s, 1, 0.1);
        } else {
          a.angle += dt * 0.5;
          a.target.set(a.center.x + Math.cos(a.angle) * 22, bounds.y, a.center.z + Math.sin(a.angle) * 22);
          a.s = THREE.MathUtils.lerp(a.s, 1, 0.1);
        }
        moveToward(a, speed, dt);
        a.phase += dt * 6;
        a.y = bounds.y + Math.abs(Math.sin(a.phase)) * 0.14;
      } else if (behavior === 'stalk') {
        let speed = a.speed;
        const prey = shared.herds.donkeys;
        if (now > a.retargetAt) {
          a.retargetAt = now + 5 + rand() * 5;
          let best = a.center, bd = Infinity;
          for (const cc of CLEARINGS) { const d = cc.distanceToSquared(a.pos); if (d < bd) { bd = d; best = cc; } }
          a.center = best;
          if (prey.length && rand() > 0.5) a.dashUntil = now + 2.6;
        }
        if (now < a.dashUntil && prey.length) {
          let tp = null, bd = Infinity;
          for (const pr of prey) { const d = pr.distanceToSquared(a.pos); if (d < bd) { bd = d; tp = pr; } }
          if (tp) { a.target.set(tp.x, bounds.y, tp.z); speed = a.speed * 2.4; }
        } else {
          a.angle += dt * 0.5;
          const rr = 22 + Math.sin(now * 0.5) * 5;
          a.target.set(a.center.x + Math.cos(a.angle) * rr, bounds.y, a.center.z + Math.sin(a.angle) * rr);
        }
        moveToward(a, speed, dt);
        a.phase += dt * 4;
        a.y = bounds.y + Math.abs(Math.sin(a.phase)) * 0.1;
      } else if (behavior === 'swarm') {
        let art = ARTIFACTS[0], bd = Infinity;
        for (const ar of ARTIFACTS) { const d = ar.distanceToSquared(a.pos); if (d < bd) { bd = d; art = ar; } }
        a.angle += dt * 2.2;
        const rr = 3 + Math.sin(now * 2 + a.phase) * 2.2;
        a.target.set(art.x + Math.cos(a.angle) * rr, bounds.y, art.z + Math.sin(a.angle) * rr);
        let sp = a.speed;
        // Displacement: scatter away from a whale breach
        if (now < shared.breachUntil) {
          const dx = a.pos.x - shared.breachX, dz = a.pos.z - shared.breachZ;
          if (dx * dx + dz * dz < 40 * 40) {
            const dl = Math.hypot(dx, dz) || 1;
            a.target.set(a.pos.x + (dx / dl) * 20, bounds.y, a.pos.z + (dz / dl) * 20);
            sp = a.speed * 2.6;
          }
        }
        moveToward(a, sp, dt);
        a.phase += dt * 8;
        a.rotZ = Math.sin(a.phase) * 0.3;
        a.y = bounds.y + Math.sin(a.phase * 0.5) * 0.4;
      } else if (behavior === 'swim') {
        a.pos.x += a.dir * a.speed * dt;
        if (a.pos.x > bounds.xMax) a.dir = -1;
        if (a.pos.x < bounds.xMin) a.dir = 1;
        a.phase += dt * 6;
        a.yaw = a.dir > 0 ? Math.PI / 2 : -Math.PI / 2;
        a.rotZ = Math.sin(a.phase) * 0.2;
        const leap = Math.max(0, Math.sin(a.phase * 0.5)) * 0.6;
        a.y = bounds.y + leap;
        // Displacement: scatter away from a whale breach
        if (now < shared.breachUntil) {
          const dx = a.pos.x - shared.breachX, dz = a.pos.z - shared.breachZ;
          if (dx * dx + dz * dz < 44 * 44) {
            const dl = Math.hypot(dx, dz) || 1;
            a.pos.x += (dx / dl) * a.speed * 2.4 * dt;
            a.pos.z = THREE.MathUtils.clamp(a.pos.z + (dz / dl) * a.speed * 2.4 * dt, bounds.zMin - 5, bounds.zMax + 5);
            a.rotZ = Math.sin(now * 22) * 0.5;
          }
        }
        if (speciesKey === 'dolphin' && a.pos.z > shared.dolphinMaxZ) shared.dolphinMaxZ = a.pos.z;
      } else if (behavior === 'whale') {
        a.pos.x += a.dir * a.speed * dt;
        if (a.pos.x > bounds.xMax) a.dir = -1;
        if (a.pos.x < bounds.xMin) a.dir = 1;
        a.yaw = a.dir > 0 ? Math.PI / 2 : -Math.PI / 2;
        a.phase += dt;
        a.pos.z = a.baseZ + Math.sin(a.phase * 0.3) * 2.5;
        if (now > a.retargetAt) {
          a.retargetAt = now + 14 + rand() * 12;
          a.dashUntil = now + 2.4;
          shared.breachX = a.pos.x; shared.breachZ = a.pos.z;
          shared.breachUntil = now + 1.5; shared.breachSeq += 1;
        }
        if (now < a.dashUntil) {
          const tt = 1 - (a.dashUntil - now) / 2.4;
          const arc = Math.sin(tt * Math.PI);
          a.y = bounds.y + arc * 6.5;
          a.rotX = -arc * 0.7;
        } else {
          a.y = bounds.y + Math.sin(a.phase * 0.6) * 0.25;
          a.rotX = 0;
        }
      } else if (behavior === 'eel') {
        a.phase += dt;
        if (now > a.retargetAt) {
          a.retargetAt = now + 0.7 + rand() * 1.3;
          a.dir = rand() > 0.5 ? 1 : -1;
          a.speed = 8 + rand() * 9;
        }
        a.pos.x += a.dir * a.speed * dt;
        if (a.pos.x > bounds.xMax) a.dir = -1;
        if (a.pos.x < bounds.xMin) a.dir = 1;
        a.yaw = a.dir > 0 ? Math.PI / 2 : -Math.PI / 2;
        a.rotZ = Math.sin(now * 12 + a.phase) * 0.5;
        a.pos.z = a.baseZ + Math.sin(now * 3 + a.phase) * 1.6;
        a.y = bounds.y + Math.sin(a.phase * 2) * 0.2;
      } else if (behavior === 'school') {
        a.angle += dt * 1.6;
        const rr = 1.4 + Math.sin(now * 3 + a.phase) * 1.0;
        a.target.set(a.center.x + Math.cos(a.angle) * rr, bounds.y, a.center.z + Math.sin(a.angle) * rr);
        moveToward(a, a.speed, dt);
        a.phase += dt * 12;
        a.rotZ = Math.sin(a.phase) * 0.3;
        a.y = bounds.y + Math.sin(a.phase * 0.5) * 0.12;
      } else if (behavior === 'guardWall') {
        // Barrier Logic: elephants form a wall when a sideliner dolphin nears the shore
        if (shared.dolphinMaxZ > 8) {
          const wx = THREE.MathUtils.clamp(WORLD.xMin + 45 + i * 15, WORLD.xMin + 35, WORLD.xMax - 35);
          a.target.set(wx, bounds.y, 30);
          moveToward(a, a.speed * 2, dt);
          a.yaw = THREE.MathUtils.lerp(a.yaw, Math.PI, 0.08);
          a.phase += dt * 3;
          a.y = bounds.y + Math.abs(Math.sin(a.phase)) * 0.1;
        } else {
          if (a.target.distanceTo(a.pos) < 1.4) {
            a.target.set(THREE.MathUtils.lerp(bounds.xMin, bounds.xMax, rand()), bounds.y, THREE.MathUtils.lerp(bounds.zMin, bounds.zMax, rand()));
          }
          moveToward(a, a.speed, dt);
          a.phase += dt * 4;
          a.y = bounds.y + Math.abs(Math.sin(a.phase)) * 0.12;
        }
      } else if (behavior === 'patrol') {
        a.pos.x += a.dir * a.speed * dt;
        if (a.pos.x > bounds.xMax) a.dir = -1;
        if (a.pos.x < bounds.xMin) a.dir = 1;
        a.phase += dt * 3;
        a.yaw = a.dir > 0 ? Math.PI / 2 : -Math.PI / 2;
        const cam = shared.camPos;
        const near = Math.abs(cam.x - a.pos.x) < 12 && Math.abs(cam.z - a.baseZ) < 12;
        a.snap = THREE.MathUtils.lerp(a.snap, near ? 1 : 0, 0.15);
        a.pos.z = a.baseZ + Math.sin(a.phase) * 0.6;
        a.y = bounds.y;
        a.s = 1 + a.snap * (0.12 + Math.abs(Math.sin(now * 12)) * 0.12);
      } else if (behavior === 'canopyfly') {
        // Echo Effect: gather above the briefed zone during a briefing
        if (echoActive) {
          a.center.x = THREE.MathUtils.lerp(a.center.x, shared.briefX, 0.03);
          a.r = THREE.MathUtils.lerp(a.r, 7, 0.04);
          a.baseY = THREE.MathUtils.lerp(a.baseY, 20, 0.03);
          a.angle += dt * 1.5;
        } else {
          a.r = THREE.MathUtils.lerp(a.r, a.baseR, 0.02);
          a.angle += dt * 0.6;
        }
        a.pos.x = a.center.x + Math.cos(a.angle) * a.r;
        a.pos.z = a.center.z + Math.sin(a.angle) * a.r;
        a.yaw = -a.angle;
        a.phase += dt * 10;
        a.y = a.baseY + Math.sin(a.phase) * 0.6;
        a.rotZ = Math.sin(a.angle) * 0.3;
      } else if (behavior === 'perch') {
        a.phase += dt * 1.5;
        a.y = a.baseY + Math.sin(a.phase) * 0.3;
        a.rotZ = Math.sin(a.phase * 0.5) * 0.15;
        if (pop) a.s = 1 + Math.max(0, Math.sin(now * 0.7 + a.phase) - 0.9) * 3;
      } else {
        // wander (land) — grazers scatter when a predator closes in
        let speed = a.speed;
        if (flee) {
          let px = null, pz = null, bd = 20 * 20;
          for (const pr of shared.predators) { const dx = a.pos.x - pr.x, dz = a.pos.z - pr.z; const d = dx * dx + dz * dz; if (d < bd) { bd = d; px = pr.x; pz = pr.z; } }
          if (px !== null) {
            const dx = a.pos.x - px, dz = a.pos.z - pz, dl = Math.hypot(dx, dz) || 1;
            a.target.set(
              THREE.MathUtils.clamp(a.pos.x + (dx / dl) * 30, bounds.xMin, bounds.xMax),
              bounds.y,
              THREE.MathUtils.clamp(a.pos.z + (dz / dl) * 30, bounds.zMin, bounds.zMax)
            );
            speed = a.speed * 2.8;
          }
        }
        if (!flee || speed === a.speed) {
          if (a.target.distanceTo(a.pos) < 1.4) {
            a.target.set(
              THREE.MathUtils.lerp(bounds.xMin, bounds.xMax, rand()),
              bounds.y,
              THREE.MathUtils.lerp(bounds.zMin, bounds.zMax, rand())
            );
          }
        }
        moveToward(a, speed, dt);
        a.phase += dt * (speed > a.speed * 1.5 ? 9 : 4);
        a.y = bounds.y + Math.abs(Math.sin(a.phase)) * 0.12;
      }

      body.position.set(a.pos.x, a.y, a.pos.z);
      body.rotation.set(a.rotX, a.yaw, a.rotZ);
      body.scale.setScalar(a.s);
      body.updateMatrix();
      for (let pi = 0; pi < parts.length; pi++) {
        M.multiplyMatrices(body.matrix, localMats[pi]);
        refs.current[pi].setMatrixAt(i, M);
      }
    }
    for (let pi = 0; pi < parts.length; pi++) refs.current[pi].instanceMatrix.needsUpdate = true;
  });

  return (
    <group>
      {parts.map((pt, pi) => (
        <instancedMesh
          key={pi}
          ref={(el) => (refs.current[pi] = el)}
          args={[undefined, undefined, count]}
          castShadow={!!pt.shadow}
          frustumCulled={false}
        >
          <Geo shape={pt.shape} args={pt.args} />
          <meshStandardMaterial
            color={pt.color}
            emissive={pt.emissive || '#000000'}
            emissiveIntensity={pt.ei || 0}
            metalness={pt.metalness || 0}
            roughness={pt.roughness ?? 0.85}
            flatShading
            toneMapped={!pt.emissive}
          />
        </instancedMesh>
      ))}
    </group>
  );
}
